import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Radar, Loader2, MapPin, Target, CheckCircle2, Wifi, Sparkles, FileDown, Sprout, MessageSquareWarning, ThumbsUp, Factory, Search, Plus } from "lucide-react";
import { toast } from "sonner";
import { api } from "../../lib/api";
import { computeHealth } from "../../lib/healthScore";
import BusinessLookup from "./BusinessLookup";
import ProspectReportsStrip from "./ProspectReportsStrip";
import RecoveryStep from "./RecoveryStep";

const inputCls =
  "w-full rounded-xl border border-[#1e3d3a]/15 bg-[#eae7de]/60 px-4 py-3 text-sm text-[#1e3d3a] placeholder:text-[#1e3d3a]/35 focus:border-[#c9a961] focus:outline-none focus:ring-2 focus:ring-[#c9a961]/20 transition-colors duration-300";

function Roadmap({ target, phases, scanId, onDownload, downloading, clientName, onClientNameChange }) {
  return (
    <div>
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <span className="text-[10px] uppercase tracking-[0.25em] text-[#1e3d3a]/55">Recovery roadmap · {target}</span>
        {onDownload && (
          <div className="flex items-center gap-2">
            {onClientNameChange && (
              <input
                data-testid="admin-roadmap-client-name"
                type="text"
                value={clientName || ""}
                onChange={(e) => onClientNameChange(e.target.value)}
                placeholder="Owner name for signature (optional)"
                className="w-56 rounded-full border border-[#1e3d3a]/15 bg-white px-3 py-1.5 text-xs text-[#1e3d3a] placeholder:text-[#1e3d3a]/35 focus:border-[#c9a961] focus:outline-none focus:ring-2 focus:ring-[#c9a961]/20"
              />
            )}
            <button
              type="button"
              data-testid="admin-roadmap-pdf"
              onClick={onDownload}
              disabled={downloading}
              className="inline-flex items-center gap-1.5 rounded-full border border-[#1e3d3a]/15 bg-white px-3 py-1.5 text-[11px] uppercase tracking-[0.2em] text-[#1e3d3a] hover:border-[#c9a961] hover:text-[#a3823f] transition-colors duration-300 disabled:opacity-60"
            >
              {downloading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <FileDown className="h-3.5 w-3.5" />}
              {downloading ? "Building…" : "Download PDF"}
            </button>
          </div>
        )}
      </div>
      <div className="mt-4 relative pl-8">
        <div className="absolute left-[7px] top-2 bottom-2 w-px bg-[#1e3d3a]/10" />
        {phases?.map((ph, i) => (
          <div key={i} data-testid={`admin-roadmap-phase-${i}`} className="relative mb-5">
            <span className="absolute -left-8 top-1.5 h-4 w-4 rounded-full border-2 border-[#c9a961] bg-white" />
            <div className="rounded-2xl border border-[#1e3d3a]/12 bg-white p-5">
              <div className="flex items-center justify-between gap-3">
                <h4 className="font-head text-lg text-[#1e3d3a]">{ph.phase}</h4>
                <span className="text-xs text-[#1e3d3a]/45">{ph.timeframe}</span>
              </div>
              <p className="mt-2 text-sm text-[#1e3d3a]/65">{ph.objective}</p>
              <ul className="mt-3 space-y-2">
                {ph.steps?.map((st, j) => (
                  scanId ? (
                    <RecoveryStep
                      key={j}
                      stepText={st}
                      testid={`admin-roadmap-step-${i}-${j}`}
                      loadDetail={() => api.post("/bbb/roadmap/step-detail", {
                        scan_id: scanId, phase_index: i, step_index: j, step_text: st, business_name: target,
                      }).then((r) => r.data)}
                      saveItem={(itemIndex, done) => api.put("/bbb/roadmap/step-detail/checklist", {
                        scan_id: scanId, phase_index: i, step_index: j, item_index: itemIndex, done,
                      })}
                    />
                  ) : (
                    <li key={j} className="flex items-start gap-2 text-sm text-[#1e3d3a]/75">
                      <CheckCircle2 className="h-4 w-4 text-[#4b7a3a] shrink-0 mt-0.5" /> {st}
                    </li>
                  )
                ))}
              </ul>
              <p className="mt-3 text-xs text-[#a3823f]">Milestone: {ph.milestone}</p>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

const RATING_OPTIONS = ["F", "D-", "D", "D+", "C-", "C", "C+", "B-", "B", "B+", "A-", "A", "A+", "NR"];

function ReviewCard({ r, tone }) {
  const border = tone === "neg" ? "border-l-[#b23a48]" : "border-l-[#4b7a3a]";
  return (
    <div className={`rounded-r-xl border border-[#1e3d3a]/10 border-l-2 ${border} bg-white px-3 py-2`}>
      <p className="text-xs text-[#1e3d3a]/80 leading-relaxed">“{r.excerpt}”</p>
      <p className="mt-1 text-[9px] uppercase tracking-[0.16em] text-[#1e3d3a]/40">
        {[r.theme, r.source, r.date_hint].filter(Boolean).join(" · ")}
      </p>
    </div>
  );
}

function ProspectIntel({ ledger, vendors, reported }) {
  const negatives = ledger?.negative_reviews || [];
  const positives = ledger?.positive_reviews || [];
  const hasVendors = (vendors || []).length > 0;
  if (!reported && !negatives.length && !positives.length && !hasVendors) return null;
  const totalOutsourced = (vendors || []).reduce((s, v) => s + (Number(v.est_monthly_cost) || 0), 0);
  return (
    <div className="mt-4 space-y-4" data-testid="prospect-intel">
      {reported && (
        <div data-testid="prospect-reported-reviews" className="rounded-2xl border border-[#b23a48]/25 bg-white p-5">
          <span className="text-[10px] uppercase tracking-[0.22em] text-[#b23a48] flex items-center gap-1.5">
            <MessageSquareWarning className="h-3.5 w-3.5" /> Reported through the BBB · negative impact
          </span>
          {reported.loading && (
            <p className="mt-3 text-xs text-[#1e3d3a]/55 flex items-center gap-2">
              <Loader2 className="h-3.5 w-3.5 animate-spin" /> Pulling reported complaints from this business's live BBB profile…
            </p>
          )}
          {!reported.loading && (reported.reviews || []).length === 0 && (
            <p className="mt-3 text-xs text-[#1e3d3a]/50">
              No complaint text could be pulled from this profile's BBB pages — the illustrative negative reviews below still apply.
            </p>
          )}
          {!reported.loading && (reported.reviews || []).length > 0 && (
            <div className="mt-3 space-y-2">
              {reported.reviews.map((r, i) => (
                <ReviewCard key={i} r={{ ...r, source: r.source || "BBB · reported" }} tone="neg" />
              ))}
              {reported.source_url && (
                <p className="text-[9px] text-[#1e3d3a]/35 break-all">Source: {reported.source_url}</p>
              )}
            </div>
          )}
        </div>
      )}
      {(negatives.length > 0 || positives.length > 0) && (
        <div data-testid="prospect-review-ledger" className="rounded-2xl border border-[#1e3d3a]/12 bg-white p-5">
          <span className="text-[10px] uppercase tracking-[0.22em] text-[#1e3d3a]/55">Full review ledger</span>
          {negatives.length > 0 && (
            <div className="mt-3">
              <p className="text-[10px] uppercase tracking-[0.18em] text-[#b23a48] flex items-center gap-1.5">
                <MessageSquareWarning className="h-3 w-3" /> Negative ({negatives.length})
              </p>
              <div className="mt-2 space-y-2">
                {negatives.map((r, i) => <ReviewCard key={i} r={r} tone="neg" />)}
              </div>
            </div>
          )}
          {positives.length > 0 && (
            <div className="mt-4">
              <p className="text-[10px] uppercase tracking-[0.18em] text-[#4b7a3a] flex items-center gap-1.5">
                <ThumbsUp className="h-3 w-3" /> Positive ({positives.length})
              </p>
              <div className="mt-2 space-y-2">
                {positives.map((r, i) => <ReviewCard key={i} r={r} tone="pos" />)}
              </div>
            </div>
          )}
        </div>
      )}
      {hasVendors && (
        <div data-testid="prospect-vendor-map" className="rounded-2xl border border-[#1e3d3a]/12 bg-white p-5 overflow-x-auto">
          <span className="text-[10px] uppercase tracking-[0.22em] text-[#1e3d3a]/55 flex items-center gap-1.5">
            <Factory className="h-3.5 w-3.5 text-[#a3823f]" /> Vendor &amp; outsourcing map
          </span>
          <p className="mt-1 text-[11px] text-[#1e3d3a]/50">
            Who they likely outsource to in order to meet business requirements — and Elevare's move on each.
          </p>
          <table className="mt-3 w-full text-left text-xs">
            <thead>
              <tr className="text-[9px] uppercase tracking-[0.16em] text-[#1e3d3a]/45 border-b border-[#1e3d3a]/10">
                <th className="py-1.5 pr-2">Vendor</th>
                <th className="py-1.5 pr-2">Category</th>
                <th className="py-1.5 pr-2">Outsourced service</th>
                <th className="py-1.5 pr-2">Est./mo</th>
                <th className="py-1.5">Elevare action</th>
              </tr>
            </thead>
            <tbody>
              {vendors.map((v, i) => (
                <tr key={i} className="border-b border-[#1e3d3a]/6 align-top">
                  <td className="py-2 pr-2 font-medium text-[#1e3d3a]">{v.vendor}</td>
                  <td className="py-2 pr-2 text-[#1e3d3a]/60">{v.category}</td>
                  <td className="py-2 pr-2 text-[#1e3d3a]/60">{v.service}</td>
                  <td className="py-2 pr-2 text-[#1e3d3a]/80 whitespace-nowrap">{v.est_monthly_cost ? `$${Number(v.est_monthly_cost).toLocaleString()}` : "—"}</td>
                  <td className="py-2 text-[#a3823f]">{v.elevare_action}</td>
                </tr>
              ))}
              <tr>
                <td className="py-2 pr-2 font-semibold text-[#1e3d3a]" colSpan={3}>Total outsourced spend</td>
                <td className="py-2 pr-2 font-semibold text-[#1e3d3a] whitespace-nowrap">${totalOutsourced.toLocaleString()}</td>
                <td />
              </tr>
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export default function AdminProspector({ lookupPrefill }) {
  const [view, setView] = useState("scan");
  const [industry, setIndustry] = useState("Hospitality");
  const [region, setRegion] = useState("Texas, USA");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [liveConfigured, setLiveConfigured] = useState(false);
  const [live, setLive] = useState(false);
  const [liveResults, setLiveResults] = useState(null);
  const [liveInfo, setLiveInfo] = useState(null);
  const [liveStatus, setLiveStatus] = useState("");
  const [ratingsFilter, setRatingsFilter] = useState([]);
  const [aiFocus, setAiFocus] = useState("distressed");
  const [roadmap, setRoadmap] = useState(null);
  const [roadmapFor, setRoadmapFor] = useState(null);
  const [downloading, setDownloading] = useState(false);
  const [picked, setPicked] = useState(new Set());
  const [savingLeads, setSavingLeads] = useState(false);
  const [savingOutreach, setSavingOutreach] = useState(false);

  const togglePick = (key) => setPicked((s) => {
    const n = new Set(s);
    n.has(key) ? n.delete(key) : n.add(key);
    return n;
  });
  const saveToLeads = async () => {
    const items = (liveResults || []).filter((p) => picked.has(p.company_name)).map((p) => ({
      company_name: p.company_name, location: p.location || null,
      bbb_rating: p.bbb_rating || null, complaints: p.complaints ?? null,
      accredited: typeof p.accredited === "boolean" ? p.accredited : null,
      health_score: computeHealth(p, null),
      url: p.url || null, industry: industry === "__all__" ? null : industry,
    }));
    if (!items.length) return;
    setSavingLeads(true);
    try {
      const { data } = await api.post("/admin/leads/bulk-from-prospect", { items });
      toast.success(`Saved ${data.added} to Leads${data.skipped ? ` · ${data.skipped} already in pipeline` : ""}.`);
      setPicked(new Set());
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save to Leads.");
    } finally {
      setSavingLeads(false);
    }
  };
  const saveToOutreach = async () => {
    const items = (liveResults || []).filter((p) => picked.has(p.company_name)).map((p) => ({
      company_name: p.company_name, location: p.location || null,
      bbb_rating: p.bbb_rating || null, complaints: p.complaints ?? null,
      health_score: (computeHealth(p, null) || {}).score ?? null,
      url: p.url || null, industry: industry === "__all__" ? null : industry,
    }));
    if (!items.length) return;
    setSavingOutreach(true);
    try {
      const { data } = await api.post("/admin/outreach/from-prospect", { items });
      toast.success(`Sent ${data.added} to Outreach Contacts${data.skipped ? ` · ${data.skipped} already tracked` : ""}.`);
      setPicked(new Set());
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not send to Outreach.");
    } finally {
      setSavingOutreach(false);
    }
  };
  const [clientName, setClientName] = useState("");
  const [reported, setReported] = useState(null);

  // When a business is sent over from the Outreach Engine, jump to the lookup view.
  useEffect(() => {
    if (lookupPrefill && lookupPrefill.ts) setView("lookup");
  }, [lookupPrefill?.ts]);

  const toggleRating = (r) =>
    setRatingsFilter((prev) => (prev.includes(r) ? prev.filter((x) => x !== r) : [...prev, r]));

  useEffect(() => {
    api.get("/bbb/status").then(({ data }) => {
      setLiveConfigured(data.configured);
      setLive(data.configured);
    }).catch(() => {});
  }, []);

  const scanAI = async () => {
    const ind = industry === "__all__" ? "a mix of local industries" : industry;
    const { data } = await api.post("/prospect", { industry: ind, region, count: 4, mode: aiFocus });
    setResult(data.result);
  };

  const scanLive = async () => {
    setLiveStatus(aiFocus === "new_business"
      ? "Searching the BBB and checking founding dates on each profile — this can take a couple of minutes…"
      : "Searching the Better Business Bureau…");
    const { data } = await api.post("/bbb/live-search", {
      query: industry, location: region, max_items: 25,
      ratings: ratingsFilter.length ? ratingsFilter : undefined,
      focus: aiFocus,
    });
    setLiveResults(data.results || []);
    setLiveInfo({ total: data.total, distressed: data.distressed, matched: data.matched,
                  ratings_filter: data.ratings_filter, focus: data.focus, checked: data.checked });
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!region.trim()) { toast.error("Enter a region to scan."); return; }
    setLoading(true);
    setResult(null); setLiveResults(null); setLiveInfo(null); setRoadmap(null); setRoadmapFor(null); setReported(null); setPicked(new Set());
    try {
      if (live && liveConfigured) { await scanLive(); toast.success("Live BBB results in."); }
      else { await scanAI(); toast.success("Prospects surfaced."); }
    } catch {
      toast.error("Scan failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const buildRoadmap = async (company) => {
    setRoadmapFor(company.company_name);
    setRoadmap(null);
    // Live prospects carry a real BBB profile URL — pull their actual
    // reported complaints in parallel with the roadmap build.
    if (company.url && company.url.includes("bbb.org")) {
      setReported({ loading: true });
      api.post("/bbb/reported-reviews", { url: company.url, company_name: company.company_name })
        .then(({ data }) => setReported({ reviews: data.reviews || [], source_url: data.source_url }))
        .catch(() => setReported({ reviews: [], source_url: null }));
    } else {
      setReported(null);
    }
    try {
      const { data } = await api.post("/bbb/roadmap", {
        company_name: company.company_name,
        location: company.location,
        bbb_rating: company.bbb_rating,
        complaints: typeof company.complaints === "number" ? company.complaints : null,
        industry,
      });
      setRoadmap({
        target: company.company_name,
        location: company.location,
        bbb_rating: company.bbb_rating,
        scanId: data.scan_id,
        ...data.result,
      });
    } catch {
      toast.error("Could not build roadmap.");
      setRoadmapFor(null);
    }
  };

  const downloadPDF = async ({ company_name, location, bbb_rating, summary, category_scores, phases, financial_snapshot, review_snapshot, expense_projection, review_ledger, vendor_analysis }) => {
    setDownloading(true);
    try {
      const mergedNegative = [
        ...((reported?.reviews || []).map((r) => ({ excerpt: r.excerpt, theme: r.theme || null, source: r.source || "BBB · reported complaint" }))),
        ...((review_ledger?.negative_reviews || [])),
      ];
      const res = await api.post(
        "/prospect/assessment-pdf",
        {
          company_name,
          location: location || null,
          bbb_rating: bbb_rating || null,
          industry,
          summary: summary || null,
          expense_projection: expense_projection || null,
          positive_reviews: review_ledger?.positive_reviews || [],
          negative_reviews: mergedNegative,
        },
        { responseType: "blob" }
      );
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      const safe = (company_name || "assessment").replace(/[^A-Za-z0-9_-]+/g, "_");
      a.download = `Elevare_Assessment_${safe}.pdf`;
      a.rel = "noopener";
      document.body.appendChild(a);
      a.click();
      // Defer cleanup so the browser can finish reading the blob (revoking
      // synchronously cancels the download in Safari / some browsers).
      setTimeout(() => { a.remove(); window.URL.revokeObjectURL(url); }, 2000);
      toast.success("PDF downloaded.");
    } catch {
      toast.error("Could not build PDF.");
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div data-testid="admin-prospector">
      <ProspectReportsStrip />
      {/* Top view switch — market scan vs. individual business lookup */}
      <div className="mb-5 inline-flex rounded-full border border-[#1e3d3a]/15 bg-[#eae7de]/60 p-1" data-testid="prospector-view-switch">
        <button
          type="button"
          data-testid="prospector-view-scan"
          onClick={() => setView("scan")}
          className={`flex items-center gap-2 rounded-full px-4 py-2 text-xs transition-colors duration-300 ${view === "scan" ? "bg-[#1e3d3a] text-white" : "text-[#1e3d3a]/60 hover:text-[#1e3d3a]"}`}
        >
          <Radar className="h-3.5 w-3.5" /> Market scan
        </button>
        <button
          type="button"
          data-testid="prospector-view-lookup"
          onClick={() => setView("lookup")}
          className={`flex items-center gap-2 rounded-full px-4 py-2 text-xs transition-colors duration-300 ${view === "lookup" ? "bg-[#1e3d3a] text-white" : "text-[#1e3d3a]/60 hover:text-[#1e3d3a]"}`}
        >
          <Search className="h-3.5 w-3.5" /> Business lookup
        </button>
      </div>

      {view === "lookup" ? (
        <BusinessLookup prefill={lookupPrefill} />
      ) : (
      <div data-testid="admin-prospector-scan">
      <div className="rounded-2xl border border-[#1e3d3a]/12 bg-white p-6">
        <div className="flex items-center gap-3">
          <Radar className="h-4 w-4 text-[#c9a961]" />
          <span className="text-[10px] uppercase tracking-[0.28em] text-[#1e3d3a]/60">AI prospector</span>
        </div>
        <p className="mt-3 text-sm text-[#1e3d3a]/65 leading-relaxed max-w-2xl">
          Point the scanner at an industry and region. Surfaces underperforming businesses on the BBB, then
          builds a phased recovery roadmap you can pitch to the owner.
        </p>

        <div className="mt-6 inline-flex rounded-full border border-[#1e3d3a]/15 bg-[#eae7de]/60 p-1">
          <button
            data-testid="admin-mode-live"
            onClick={() => liveConfigured && setLive(true)}
            disabled={!liveConfigured}
            className={`flex items-center gap-2 rounded-full px-4 py-2 text-xs transition-colors duration-300 ${live ? "bg-[#1e3d3a] text-white" : "text-[#1e3d3a]/60 hover:text-[#1e3d3a]"} ${!liveConfigured ? "opacity-40 cursor-not-allowed" : ""}`}
          >
            <Wifi className="h-3.5 w-3.5" /> Live BBB feed
          </button>
          <button
            data-testid="admin-mode-ai"
            onClick={() => setLive(false)}
            className={`flex items-center gap-2 rounded-full px-4 py-2 text-xs transition-colors duration-300 ${!live ? "bg-[#1e3d3a] text-white" : "text-[#1e3d3a]/60 hover:text-[#1e3d3a]"}`}
          >
            <Sparkles className="h-3.5 w-3.5" /> AI simulation
          </button>
        </div>
        {!liveConfigured && (
          <p className="mt-3 text-xs text-[#1e3d3a]/45">Live BBB activates once an Apify token is connected. Simulation mode is active.</p>
        )}

        {live && liveConfigured && (
          <div className="mt-4" data-testid="admin-rating-filter">
            <span className="text-[10px] uppercase tracking-[0.22em] text-[#1e3d3a]/50">
              Filter by BBB rating {ratingsFilter.length === 0 && <span className="normal-case tracking-normal">· none selected — lowest-rated shown first</span>}
            </span>
            <div className="mt-2 flex flex-wrap gap-1.5">
              {RATING_OPTIONS.map((r) => (
                <button
                  key={r}
                  type="button"
                  data-testid={`admin-rating-${r.replace("+", "plus").replace("-", "minus")}`}
                  onClick={() => toggleRating(r)}
                  className={`rounded-full border px-3 py-1 text-[11px] font-medium transition-colors duration-300 ${
                    ratingsFilter.includes(r)
                      ? "bg-[#1e3d3a] text-white border-[#1e3d3a]"
                      : "border-[#1e3d3a]/15 text-[#1e3d3a]/55 hover:border-[#c9a961] hover:text-[#a3823f]"
                  }`}
                >
                  {r === "NR" ? "Not rated" : r}
                </button>
              ))}
              {ratingsFilter.length > 0 && (
                <button
                  type="button"
                  data-testid="admin-rating-clear"
                  onClick={() => setRatingsFilter([])}
                  className="rounded-full px-3 py-1 text-[10px] uppercase tracking-[0.18em] text-[#b23a48]/80 hover:text-[#b23a48] transition-colors duration-300"
                >
                  Clear
                </button>
              )}
            </div>
          </div>
        )}

        {/* Focus toggle — applies to BOTH the live BBB feed and AI simulation */}
        <div className="mt-4 inline-flex rounded-full border border-[#1e3d3a]/15 bg-[#eae7de]/60 p-1" data-testid="admin-ai-focus">
          <button
            type="button"
            data-testid="admin-focus-distressed"
            onClick={() => setAiFocus("distressed")}
            className={`rounded-full px-4 py-1.5 text-[11px] transition-colors duration-300 ${aiFocus === "distressed" ? "bg-[#1e3d3a] text-white" : "text-[#1e3d3a]/60 hover:text-[#1e3d3a]"}`}
          >
            Distressed · C or below
          </button>
          <button
            type="button"
            data-testid="admin-focus-new"
            onClick={() => setAiFocus("new_business")}
            className={`flex items-center gap-1.5 rounded-full px-4 py-1.5 text-[11px] transition-colors duration-300 ${aiFocus === "new_business" ? "bg-[#1e3d3a] text-white" : "text-[#1e3d3a]/60 hover:text-[#1e3d3a]"}`}
          >
            <Sprout className="h-3 w-3" /> New · open &lt; 1 year
          </button>
        </div>
        {live && aiFocus === "new_business" && (
          <p className="mt-2 text-[11px] text-[#1e3d3a]/45 max-w-2xl">
            Live mode checks each profile's "Business Started" date on the BBB and keeps only businesses under a year old — scans take longer and depend on what the BBB exposes.
          </p>
        )}

        <form onSubmit={submit} data-testid="admin-prospect-form" className="mt-6 flex flex-col sm:flex-row gap-3 max-w-3xl">
          <select data-testid="admin-prospect-industry" className={inputCls} value={industry} onChange={(e) => setIndustry(e.target.value)}>
            <option value="__all__">All categories</option>
            {["Hospitality", "Retail", "Construction", "Automotive", "Healthcare", "Home Services", "Manufacturing", "Professional Services"].map((o) => (
              <option key={o} value={o}>{o}</option>
            ))}
          </select>
          <input data-testid="admin-prospect-region" className={inputCls} value={region} onChange={(e) => setRegion(e.target.value)} placeholder="Region, e.g. Dallas, TX" />
          <button
            type="submit"
            data-testid="admin-prospect-submit"
            disabled={loading}
            className="shrink-0 rounded-full bg-[#1e3d3a] text-white px-7 py-3 text-sm font-semibold hover:bg-[#c9a961] hover:text-[#1e3d3a] transition-colors duration-300 disabled:opacity-60 flex items-center justify-center gap-2"
          >
            {loading ? (<><Loader2 className="h-4 w-4 animate-spin" /> Scanning…</>) : "Scan the market"}
          </button>
        </form>
      </div>

      {live && loading && (
        <div data-testid="admin-live-loading" className="mt-6 rounded-2xl border border-[#1e3d3a]/12 bg-white p-10 flex flex-col items-center justify-center gap-4 min-h-[240px]">
          <Loader2 className="h-8 w-8 text-[#c9a961] animate-spin" />
          <p className="text-[#1e3d3a]/60 text-sm">{liveStatus || "Scanning BBB…"}</p>
          <p className="text-[#1e3d3a]/40 text-xs text-center max-w-sm">Pulling live profiles from the Better Business Bureau — can take up to a minute.</p>
        </div>
      )}

      <AnimatePresence mode="wait">
        {live && liveResults && !loading && (
          <motion.div
            key="live"
            data-testid="admin-live-result"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
            className="mt-6 grid grid-cols-1 lg:grid-cols-12 gap-4"
          >
            <div className="lg:col-span-5 space-y-3">
              <span className="text-[10px] uppercase tracking-[0.22em] text-[#1e3d3a]/55">
                {liveInfo?.focus === "new_business"
                  ? "Live BBB · opened under a year"
                  : liveInfo?.ratings_filter?.length
                    ? `Live BBB · filtered to ${liveInfo.ratings_filter.map((r) => (r === "NR" ? "Not rated" : r)).join(", ")}`
                    : liveInfo?.distressed ? "Live BBB · lowest-rated first" : "Live BBB · lowest-rated nearby"}
              </span>
              {liveInfo?.focus === "new_business" && (
                <p className="text-xs text-[#1e3d3a]/50">
                  {liveInfo.matched} under a year old · founding dates found on {liveInfo.checked} of {liveInfo.total} profiles.
                </p>
              )}
              {liveInfo?.focus !== "new_business" && liveInfo?.ratings_filter?.length > 0 && (
                <p className="text-xs text-[#1e3d3a]/50">{liveInfo.matched} of {liveInfo.total} scanned match your rating filter.</p>
              )}
              {liveInfo && liveInfo.focus !== "new_business" && !liveInfo.ratings_filter?.length && !liveInfo.distressed && (
                <p className="text-xs text-[#1e3d3a]/50">No low-rated businesses found in {liveInfo.total} scanned. Showing lowest-rated as closest matches.</p>
              )}
              {liveResults.length === 0 && (
                <p className="text-sm text-[#1e3d3a]/45">
                  {liveInfo?.focus === "new_business"
                    ? "No businesses under a year old surfaced in this scan — the BBB hides founding dates on some profiles. Try another keyword/region, or use AI simulation for illustrative young prospects."
                    : liveInfo?.ratings_filter?.length
                      ? "No businesses match those ratings here. Widen the filter or try a broader region."
                      : "No businesses returned. Try a broader keyword or region."}
                </p>
              )}
              {liveResults.length > 0 && (
                <div className="flex items-center justify-between gap-2 rounded-xl border border-[#1e3d3a]/12 bg-white px-3 py-2">
                  <label className="flex items-center gap-2 text-[11px] uppercase tracking-[0.16em] text-[#1e3d3a]/60 cursor-pointer">
                    <input
                      type="checkbox"
                      data-testid="prospect-select-all"
                      checked={picked.size > 0 && picked.size === liveResults.length}
                      onChange={(e) => setPicked(e.target.checked ? new Set(liveResults.map((p) => p.company_name)) : new Set())}
                      className="h-3.5 w-3.5 accent-[#1e3d3a]"
                    />
                    {picked.size ? `${picked.size} selected` : "Select all"}
                  </label>
                  <div className="flex items-center gap-2">
                  <button
                    type="button"
                    data-testid="prospect-save-to-leads"
                    disabled={!picked.size || savingLeads}
                    onClick={saveToLeads}
                    className="inline-flex items-center gap-1.5 rounded-full bg-[#1e3d3a] text-white px-3 py-1.5 text-[10px] uppercase tracking-[0.16em] hover:bg-[#c9a961] hover:text-[#1e3d3a] disabled:opacity-40 transition-colors duration-300"
                  >
                    {savingLeads ? <Loader2 className="h-3 w-3 animate-spin" /> : <Plus className="h-3 w-3" />}
                    Save {picked.size || ""} to Leads
                  </button>
                  <button
                    type="button"
                    data-testid="prospect-send-to-outreach"
                    disabled={!picked.size || savingOutreach}
                    onClick={saveToOutreach}
                    className="inline-flex items-center gap-1.5 rounded-full border border-[#1e3d3a]/20 bg-white text-[#1e3d3a] px-3 py-1.5 text-[10px] uppercase tracking-[0.16em] hover:border-[#c9a961] disabled:opacity-40 transition-colors duration-300"
                  >
                    {savingOutreach ? <Loader2 className="h-3 w-3 animate-spin" /> : <Radar className="h-3 w-3" />}
                    Send to Outreach
                  </button>
                  </div>
                </div>
              )}
              {liveResults.map((p, i) => (
                <div key={i} className="relative">
                  <label
                    className="absolute left-3 top-3 z-10 flex items-center cursor-pointer"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <input
                      type="checkbox"
                      data-testid={`prospect-pick-${i}`}
                      checked={picked.has(p.company_name)}
                      onChange={() => togglePick(p.company_name)}
                      className="h-4 w-4 accent-[#c9a961]"
                    />
                  </label>
                  <button
                    data-testid={`admin-live-company-${i}`}
                    onClick={() => buildRoadmap(p)}
                    className={`w-full text-left rounded-2xl border p-5 pl-10 transition-colors duration-300 ${roadmapFor === p.company_name ? "border-[#c9a961] bg-[#c9a961]/8" : "border-[#1e3d3a]/12 bg-white hover:border-[#1e3d3a]/30"}`}
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <h4 className="font-head text-lg text-[#1e3d3a]">{p.company_name}</h4>
                        {p.location && <p className="mt-1 text-xs text-[#1e3d3a]/50 flex items-center gap-1"><MapPin className="h-3 w-3" /> {p.location}</p>}
                        {p.opened && (
                          <p className="mt-1 text-xs text-[#4b7a3a] flex items-center gap-1">
                            <Sprout className="h-3 w-3" /> Opened {p.opened}{typeof p.months_open === "number" ? ` · ${p.months_open} mo old` : ""}
                          </p>
                        )}
                      </div>
                      {p.bbb_rating && <span className="shrink-0 text-xs rounded-full border border-[#1e3d3a]/15 px-2 py-1 text-[#1e3d3a]/70">BBB {p.bbb_rating}</span>}
                      {!p.bbb_rating && <span className="shrink-0 text-xs rounded-full border border-dashed border-[#1e3d3a]/20 px-2 py-1 text-[#1e3d3a]/45">Not rated</span>}
                    </div>
                    {(() => {
                      const h = computeHealth(p, null);
                      return h ? (
                        <div data-testid={`scan-health-${i}`} className="mt-2 inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 text-[11px] font-semibold" style={{ backgroundColor: h.color + "1a", color: h.color }}>
                          <span className="h-1.5 w-1.5 rounded-full" style={{ background: h.color }} />
                          Health {h.score} · {h.grade} · {h.label}
                        </div>
                      ) : null;
                    })()}
                    {(p.complaints !== null && p.complaints !== undefined) && (
                      <p className="mt-3 text-xs text-[#1e3d3a]/50">{p.complaints} complaints on file</p>
                    )}
                    <p className="mt-3 text-xs text-[#a3823f]">Build recovery plan →</p>
                  </button>
                </div>
              ))}
            </div>
            <div className="lg:col-span-7">
              {!roadmapFor && (
                <div className="h-full min-h-[300px] rounded-2xl border border-dashed border-[#1e3d3a]/15 bg-white/40 flex flex-col items-center justify-center text-center p-10">
                  <Target className="h-10 w-10 text-[#1e3d3a]/25" strokeWidth={1} />
                  <p className="mt-6 text-[#1e3d3a]/55 max-w-xs text-sm">Select a company to generate its recovery roadmap.</p>
                </div>
              )}
              {roadmapFor && !roadmap && (
                <div className="h-full min-h-[300px] rounded-2xl border border-[#1e3d3a]/12 bg-white flex flex-col items-center justify-center gap-4">
                  <Loader2 className="h-8 w-8 text-[#c9a961] animate-spin" />
                  <p className="text-[#1e3d3a]/60 text-sm">Building plan for {roadmapFor}…</p>
                </div>
              )}
              {roadmap && (
                <div data-testid="admin-live-roadmap">
                  <p className="mb-4 text-sm leading-relaxed text-[#1e3d3a]/75 rounded-2xl border border-[#1e3d3a]/12 bg-white p-5">{roadmap.summary}</p>
                  <Roadmap
                    target={roadmap.target}
                    phases={roadmap.recovery_roadmap}
                    scanId={roadmap.scanId}
                    downloading={downloading}
                    clientName={clientName}
                    onClientNameChange={setClientName}
                    onDownload={() => downloadPDF({
                      company_name: roadmap.target,
                      location: roadmap.location,
                      bbb_rating: roadmap.bbb_rating,
                      summary: roadmap.summary,
                      category_scores: roadmap.category_scores,
                      phases: roadmap.recovery_roadmap,
                      financial_snapshot: roadmap.financial_snapshot,
                      review_snapshot: roadmap.review_snapshot,
                      expense_projection: roadmap.expense_projection,
                      review_ledger: roadmap.review_ledger,
                      vendor_analysis: roadmap.vendor_analysis,
                    })}
                  />
                  <ProspectIntel ledger={roadmap.review_ledger} vendors={roadmap.vendor_analysis} reported={reported} />
                </div>
              )}
            </div>
          </motion.div>
        )}

        {!live && result && !loading && (
          <motion.div
            key="ai"
            data-testid="admin-prospect-result"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
            className="mt-6 grid grid-cols-1 lg:grid-cols-12 gap-4"
          >
            <div className="lg:col-span-5 space-y-3">
              <span className="text-[10px] uppercase tracking-[0.22em] text-[#1e3d3a]/55">Surfaced prospects</span>
              {result.prospects?.map((p, i) => {
                const isTarget = p.company_name === result.target;
                const isSelected = roadmapFor === p.company_name;
                return (
                  <button
                    type="button"
                    key={i}
                    data-testid={`admin-prospect-${i}`}
                    onClick={() => buildRoadmap(p)}
                    className={`w-full text-left rounded-2xl border p-5 transition-colors duration-300 ${isSelected ? "border-[#c9a961] bg-[#c9a961]/12" : isTarget ? "border-[#c9a961] bg-[#c9a961]/8 hover:border-[#c9a961]" : "border-[#1e3d3a]/12 bg-white hover:border-[#1e3d3a]/30"}`}
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <h4 className="font-head text-lg text-[#1e3d3a] flex items-center gap-2">
                          {p.company_name}
                          {isTarget && <Target className="h-4 w-4 text-[#a3823f]" />}
                        </h4>
                        <p className="mt-1 text-xs text-[#1e3d3a]/50 flex items-center gap-1"><MapPin className="h-3 w-3" /> {p.location}</p>
                        {p.opened && (
                          <p className="mt-1 text-xs text-[#4b7a3a] flex items-center gap-1">
                            <Sprout className="h-3 w-3" /> Opened {p.opened}{typeof p.months_open === "number" ? ` · ${p.months_open} mo old` : ""}
                          </p>
                        )}
                      </div>
                      <span className="shrink-0 text-xs rounded-full border border-[#1e3d3a]/15 px-2 py-1 text-[#1e3d3a]/70">
                        {p.bbb_rating && p.bbb_rating.toUpperCase() !== "NR" ? `BBB ${p.bbb_rating}` : "Not rated"}
                      </span>
                    </div>
                    <div className="mt-3 flex flex-wrap gap-2">
                      {p.distress_signals?.map((s, j) => (
                        <span key={j} className="text-[11px] rounded-full bg-[#eae7de]/70 border border-[#1e3d3a]/10 px-2.5 py-1 text-[#1e3d3a]/65">{s}</span>
                      ))}
                    </div>
                    <div className="mt-3 flex items-center justify-between text-xs">
                      <span className="text-[#1e3d3a]/50">Opportunity <span className="text-[#a3823f] font-medium">{p.opportunity_score}/100</span></span>
                      <span className="text-[#4b7a3a]">~${Number(p.estimated_annual_savings).toLocaleString()}/yr</span>
                    </div>
                    <p className="mt-3 text-xs text-[#a3823f]">{isSelected ? "Selected · plan on the right" : "Build recovery plan →"}</p>
                  </button>
                );
              })}
              <p className="text-xs text-[#1e3d3a]/40">AI-generated illustrative prospects — not verified records.</p>
            </div>
            <div className="lg:col-span-7">
              {roadmapFor && !roadmap && (
                <div className="h-full min-h-[300px] rounded-2xl border border-[#1e3d3a]/12 bg-white flex flex-col items-center justify-center gap-4">
                  <Loader2 className="h-8 w-8 text-[#c9a961] animate-spin" />
                  <p className="text-[#1e3d3a]/60 text-sm">Building plan for {roadmapFor}…</p>
                </div>
              )}
              {roadmap && (
                <div data-testid="admin-ai-roadmap">
                  <p className="mb-4 text-sm leading-relaxed text-[#1e3d3a]/75 rounded-2xl border border-[#1e3d3a]/12 bg-white p-5">{roadmap.summary}</p>
                  <Roadmap
                    target={roadmap.target}
                    phases={roadmap.recovery_roadmap}
                    scanId={roadmap.scanId}
                    downloading={downloading}
                    clientName={clientName}
                    onClientNameChange={setClientName}
                    onDownload={() => downloadPDF({
                      company_name: roadmap.target,
                      location: roadmap.location,
                      bbb_rating: roadmap.bbb_rating,
                      summary: roadmap.summary,
                      category_scores: roadmap.category_scores,
                      phases: roadmap.recovery_roadmap,
                      financial_snapshot: roadmap.financial_snapshot,
                      review_snapshot: roadmap.review_snapshot,
                      expense_projection: roadmap.expense_projection,
                      review_ledger: roadmap.review_ledger,
                      vendor_analysis: roadmap.vendor_analysis,
                    })}
                  />
                  <ProspectIntel ledger={roadmap.review_ledger} vendors={roadmap.vendor_analysis} reported={reported} />
                </div>
              )}
              {!roadmapFor && !roadmap && (() => {
                const t = result.prospects?.find((p) => p.company_name === result.target);
                return (
                  <div>
                    <Roadmap
                      target={result.target}
                      phases={result.recovery_roadmap}
                      scanId={result.scan_id}
                      downloading={downloading}
                      clientName={clientName}
                      onClientNameChange={setClientName}
                      onDownload={() => downloadPDF({
                        company_name: result.target,
                        location: t?.location,
                        bbb_rating: t?.bbb_rating,
                        summary: null,
                        phases: result.recovery_roadmap,
                        financial_snapshot: result.financial_snapshot,
                        review_snapshot: result.review_snapshot,
                        expense_projection: result.expense_projection,
                        review_ledger: result.review_ledger,
                        vendor_analysis: result.vendor_analysis,
                      })}
                    />
                    <ProspectIntel ledger={result.review_ledger} vendors={result.vendor_analysis} reported={null} />
                  </div>
                );
              })()}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
      </div>
      )}
    </div>
  );
}
